import { useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Download, ZoomIn, ZoomOut } from "lucide-react";

interface ImageLightboxProps {
    src: string;
    alt?: string;
    className?: string;
    children?: React.ReactNode;
}

export default function ImageLightbox({ src, alt, className, children }: ImageLightboxProps) {
    const [open, setOpen] = useState(false);
    const [scale, setScale] = useState(1);

    const handleZoomIn = useCallback(() => setScale(s => Math.min(s + 0.5, 4)), []);
    const handleZoomOut = useCallback(() => setScale(s => Math.max(s - 0.5, 0.5)), []);
    const handleDownload = useCallback(() => {
        const a = document.createElement('a');
        a.href = src;
        a.download = alt || 'image';
        a.target = '_blank';
        a.click();
    }, [src, alt]);

    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        // Do not close if clicking directly on the image or toolbar buttons
        if (target.tagName === 'IMG' || target.closest('button') || target.closest('[data-lightbox-toolbar]')) {
            return;
        }
        setOpen(false);
    }, []);

    const triggerElement = children || (
        <img
            src={src}
            alt={alt || 'image'}
            className={className || 'cursor-pointer hover:opacity-80 transition-opacity'}
        />
    );

    return (
        <DialogPrimitive.Root open={open} onOpenChange={(v) => { setOpen(v); if (!v) setScale(1); }}>
            <DialogPrimitive.Trigger asChild>
                {triggerElement}
            </DialogPrimitive.Trigger>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    data-testid="lightbox-overlay"
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm cursor-zoom-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                />
                <DialogPrimitive.Content
                    data-testid="lightbox-content"
                    className="fixed inset-0 z-[201] flex items-center justify-center outline-none cursor-zoom-out select-none"
                    onClick={handleBackdropClick}
                >
                    <DialogPrimitive.Title className="sr-only">{alt || 'Xem ảnh'}</DialogPrimitive.Title>
                    <DialogPrimitive.Description className="sr-only">Xem ảnh phóng to</DialogPrimitive.Description>

                    {/* Toolbar */}
                    <div
                        data-lightbox-toolbar
                        className="fixed top-4 right-4 flex items-center gap-2 z-[202] cursor-default"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={handleZoomOut}
                            className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors shadow-md cursor-pointer"
                            title="Thu nhỏ"
                        >
                            <ZoomOut className="h-4 w-4" />
                        </button>
                        <span className="text-white text-xs font-mono bg-black/60 px-2 py-1 rounded shadow-md select-none">
                            {Math.round(scale * 100)}%
                        </span>
                        <button
                            type="button"
                            onClick={handleZoomIn}
                            className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors shadow-md cursor-pointer"
                            title="Phóng to"
                        >
                            <ZoomIn className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={handleDownload}
                            className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors shadow-md cursor-pointer"
                            title="Tải ảnh về máy"
                        >
                            <Download className="h-4 w-4" />
                        </button>
                        <DialogPrimitive.Close
                            data-testid="lightbox-close-button"
                            className="p-2 rounded-full bg-rose-600/80 text-white hover:bg-rose-600 transition-colors shadow-md cursor-pointer"
                            title="Đóng (ESC hoặc click ra ngoài)"
                        >
                            <X className="h-4 w-4" />
                        </DialogPrimitive.Close>
                    </div>

                    {/* Image Container */}
                    <div
                        className="w-full h-full flex items-center justify-center p-4 cursor-zoom-out"
                        onClick={handleBackdropClick}
                    >
                        <img
                            data-testid="lightbox-image"
                            src={src}
                            alt={alt || ''}
                            className="object-contain transition-transform duration-200 cursor-default rounded-md shadow-2xl"
                            style={{ transform: `scale(${scale})`, maxWidth: '90vw', maxHeight: '90vh' }}
                            draggable={false}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}

export function isImageUrl(url: string): boolean {
    const ext = url.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
}
